const appStoreHref =
  'https://apps.apple.com/us/app/listening-text-to-speech/id1635430768';
const googlePlayHref =
  'https://play.google.com/store/apps/details?id=io.listening.main';
const chromeExtensionHref =
  'https://chromewebstore.google.com/detail/listeningcom/amjjglmomeodglgclcnjdjbhhcfnbnpd';

type ToolKind = 'text' | 'pdf' | 'web' | 'mobile' | 'extension' | 'app';

type SeoSection = {
  title: string;
  content: string;
  contentSecondary?: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

export type KeywordToolPage = {
  slug: string;
  kind: ToolKind;
  title: string;
  metaTitle: string;
  metaDescription: string;
  converterHeading: string;
  converterSubtitle: string;
  conversionLabel: string;
  faqTitle: string;
  faqDescription: string;
  faqs: Array<FaqItem>;
  seoSections: Array<SeoSection>;
  primaryCta?: {
    label: string;
    href: string;
  };
  secondaryCta?: {
    label: string;
    href: string;
  };
};

type KeywordSpec = {
  slug: string;
  phrase: string;
  angle: string;
  audience: string;
  friction: string;
  result: string;
  conversionLabel?: string;
};

function titleCase(phrase: string) {
  return phrase
    .split(' ')
    .map((word) =>
      word.length <= 3 && word === word.toUpperCase()
        ? word
        : `${word.slice(0, 1).toUpperCase()}${word.slice(1)}`,
    )
    .join(' ');
}

function buildTextPage(spec: KeywordSpec): KeywordToolPage {
  const title = titleCase(spec.phrase);
  return {
    slug: spec.slug,
    kind: 'text',
    title,
    metaTitle: `${title} - Free AI Voice Reader | Listening`,
    metaDescription: `Use Listening as a ${spec.phrase} tool for ${spec.audience}. Paste text, generate natural audio, and open a temporary read-along link with synced playback.`,
    converterHeading: title,
    converterSubtitle: `Paste text to try ${spec.phrase} with a natural AI voice. This page is tuned for ${spec.angle}, so the sample workflow is quick without hiding the full Listening app.`,
    conversionLabel: spec.conversionLabel ?? 'audio',
    faqTitle: `${title} FAQ`,
    faqDescription: `Answers for using Listening as a ${spec.phrase} tool.`,
    faqs: [
      {
        question: `How do I use this ${spec.phrase} page?`,
        answer: `Paste the text you want to hear, choose a voice, and generate audio. Listening opens a 24-hour read-along page so ${spec.audience} can follow the words while listening.`,
      },
      {
        question: `What makes this different from a basic ${spec.phrase} reader?`,
        answer: `The public tool focuses on natural narration, synced text, playback controls, and clean sharing instead of a one-time robotic preview. It is especially useful when ${spec.friction}.`,
      },
      {
        question: 'Can I use it for longer documents?',
        answer: `The free converter is best for excerpts and shorter text. For full documents, PDFs, articles, and recurring ${spec.angle} workflows, use the Listening app.`,
      },
    ],
    seoSections: [
      {
        title: `${title} for ${spec.audience}`,
        content: `${title} pages should do more than speak a sentence. Listening turns pasted text into a read-along experience with natural voices, speed control, and a temporary link you can revisit later. That matters for ${spec.audience}, where the goal is not novelty but a practical way to keep reading moving.`,
        contentSecondary: `This page is written around ${spec.angle}. Instead of sending you straight to an account screen, it puts the converter first so you can test voice quality, pacing, and readability before deciding whether the full app fits your workflow.`,
      },
      {
        title: `When ${spec.phrase} is the right choice`,
        content: `Use this workflow when ${spec.friction}. Paste the exact passage, generate audio, and listen while scanning the synced text. The combination helps catch awkward phrasing, stay focused on dense material, or turn idle time into reading time.`,
        contentSecondary: `For repeated work, Listening extends the same idea across uploaded documents, PDFs, web articles, mobile apps, and the Chrome extension, so ${spec.result}.`,
      },
    ],
  };
}

function buildPdfPage(spec: KeywordSpec): KeywordToolPage {
  const title = titleCase(spec.phrase);
  return {
    slug: spec.slug,
    kind: 'pdf',
    title,
    metaTitle: `${title} - Listen to PDFs Online | Listening`,
    metaDescription: `Try Listening for ${spec.phrase}. Upload a PDF, generate natural audio, and use a temporary read-along page with PDF text and playback controls.`,
    converterHeading: title,
    converterSubtitle: `Drop in a PDF to try ${spec.phrase}. Listening extracts readable text in your browser, then creates a natural read-along preview for ${spec.audience}.`,
    conversionLabel: 'PDF audio',
    faqTitle: `${title} FAQ`,
    faqDescription: `Common questions about using Listening for ${spec.phrase}.`,
    faqs: [
      {
        question: `Can Listening handle ${spec.phrase}?`,
        answer: `Yes. Upload a text-based PDF on this page and Listening will generate natural audio with a temporary read-along view. For Word files, scanned pages, and larger libraries, use the full Listening app.`,
      },
      {
        question: 'Does the PDF leave my browser?',
        answer:
          'The free PDF tool extracts text in your browser. We receive the extracted text to generate audio, but the PDF file itself is not uploaded by this public preview.',
      },
      {
        question: `Who is this ${spec.phrase} page best for?`,
        answer: `It is best for ${spec.audience} who want to test how a PDF sounds before committing to a longer reading workflow, especially when ${spec.friction}.`,
      },
    ],
    seoSections: [
      {
        title: `${title} without a heavy setup`,
        content: `A useful ${spec.phrase} workflow starts with the file you already have. Upload a PDF, choose a voice, and Listening opens a temporary read-along with audio and text together. That keeps the page practical for ${spec.audience} rather than turning it into another generic PDF viewer.`,
        contentSecondary: `The public preview is intentionally focused: it lets you test natural narration, academic vocabulary, and pacing on real PDF text. For full document libraries, OCR, and cross-device listening, the Listening app takes the same workflow further.`,
      },
      {
        title: `Why ${spec.phrase} needs more than system TTS`,
        content: `Dense PDFs often include citations, headings, tables, and technical vocabulary. Basic readers can sound choppy when ${spec.friction}. Listening is designed for long-form material, so the audio is easier to follow while you walk, commute, revise, or review.`,
        contentSecondary: `The goal is ${spec.result}. The converter at the top of this page gives you a fast way to test that before moving a full reading routine into the app.`,
      },
    ],
  };
}

function buildWebPage(spec: KeywordSpec): KeywordToolPage {
  const title = titleCase(spec.phrase);
  return {
    slug: spec.slug,
    kind: 'web',
    title,
    metaTitle: `${title} - Turn Web Pages Into Audio | Listening`,
    metaDescription: `Use Listening as a ${spec.phrase} tool. Paste a URL, extract the readable article text, and listen with natural AI voices and a temporary read-along page.`,
    converterHeading: title,
    converterSubtitle: `Paste an article URL to try ${spec.phrase}. Listening extracts the main readable text so ${spec.audience} can listen without page clutter.`,
    conversionLabel: 'web audio',
    faqTitle: `${title} FAQ`,
    faqDescription: `Answers about converting web pages with Listening.`,
    faqs: [
      {
        question: `How does this ${spec.phrase} tool work?`,
        answer: `Paste a URL, choose a voice, and generate audio. Listening fetches the page, extracts the main article text, and opens a 24-hour read-along link.`,
      },
      {
        question: 'What pages work best?',
        answer: `Articles, blogs, documentation, and public pages with a clear main body work best. If a page blocks extraction, paste the text into the text reader instead.`,
      },
      {
        question: `Why use Listening instead of a browser's built-in reader?`,
        answer: `Listening is built for natural audio and focused reading. It helps when ${spec.friction}, and the same content can move into app and extension workflows later.`,
      },
    ],
    seoSections: [
      {
        title: `${title} for focused web reading`,
        content: `A good ${spec.phrase} tool should not read every navigation label, cookie banner, and footer on a page. Listening focuses on the article body, turns it into audio, and pairs playback with a temporary read-along link.`,
        contentSecondary: `That makes the page useful for ${spec.audience}: paste a link, hear the content, and decide whether the article deserves deeper attention.`,
      },
      {
        title: `Use web audio when reading tabs pile up`,
        content: `This workflow is strongest when ${spec.friction}. Instead of leaving another tab unread, convert the page into audio and listen while doing low-attention work.`,
        contentSecondary: `The practical result is ${spec.result}. For daily web reading, the Listening Chrome extension extends the same idea directly into the browser.`,
      },
    ],
  };
}

function buildLinkPage(
  spec: KeywordSpec,
  kind: Extract<ToolKind, 'mobile' | 'extension' | 'app'>,
  ctas: Pick<KeywordToolPage, 'primaryCta' | 'secondaryCta'>,
): KeywordToolPage {
  const title = titleCase(spec.phrase);
  const destination =
    kind === 'extension'
      ? 'the Listening Chrome extension'
      : kind === 'mobile'
        ? 'the Listening mobile app'
        : 'the Listening app';

  return {
    slug: spec.slug,
    kind,
    title,
    metaTitle: `${title} - ${destination} | Listening`,
    metaDescription: `Find the best Listening option for ${spec.phrase}. Get the right app or extension for ${spec.audience} and keep long-form reading moving.`,
    converterHeading: title,
    converterSubtitle: `For ${spec.phrase}, the best first step is ${destination}. It keeps the workflow close to where ${spec.audience} already read.`,
    conversionLabel: 'audio',
    faqTitle: `${title} FAQ`,
    faqDescription: `Quick answers about ${spec.phrase} with Listening.`,
    faqs: [
      {
        question: `Where should I start for ${spec.phrase}?`,
        answer: `Start with ${destination}. It is the better fit when ${spec.friction}, and it connects to Listening's longer document and read-along workflows.`,
      },
      {
        question: 'Can I still try a public converter?',
        answer:
          'Yes. Listening also has public text, PDF, and web converters, but this page points you to the app or extension because that matches the keyword intent better.',
      },
      {
        question: 'Does Listening work across devices?',
        answer:
          'Yes. Listening supports web, mobile, and browser extension workflows so you can start reading on one device and continue listening elsewhere.',
      },
    ],
    seoSections: [
      {
        title: `${title} starts with the right surface`,
        content: `Some text-to-speech searches are really about where the reading happens. For ${spec.phrase}, ${destination} is the practical route because ${spec.audience} need controls that stay available while they move between documents, tabs, or devices.`,
        contentSecondary: `Listening keeps natural voices, read-along playback, and speed control connected to the rest of your library, so ${spec.result}.`,
      },
      {
        title: `Why this is not just a generic reader`,
        content: `Generic readers can be useful for a sentence or two, but they often break down when ${spec.friction}. Listening is designed for long-form reading, academic documents, and web articles that need steady narration instead of a quick accessibility toggle.`,
        contentSecondary: `Use the links at the top of this page to open the right Listening surface now, then use the public converters when you want to test a single text, PDF, or web page first.`,
      },
    ],
    ...ctas,
  };
}

const textPages = [
  buildTextPage({
    slug: 'read-aloud-reader',
    phrase: 'read aloud reader',
    angle: 'hands-free review of pasted passages',
    audience: 'students, writers, and busy readers',
    friction: 'silent reading makes it easy to skip over dense or awkward text',
    result:
      'your short-form reading can become audio without losing the text view',
  }),
  buildTextPage({
    slug: 'read-out-loud',
    phrase: 'read out loud',
    angle: 'hearing drafts, notes, and study text before sharing them',
    audience: 'people who understand better by listening',
    friction: 'you want to hear whether a passage actually flows',
    result: 'reviewing words by ear becomes part of the same reading habit',
  }),
  buildTextPage({
    slug: 'read-aloud-text',
    phrase: 'read aloud text',
    angle: 'turning pasted text into quick read-along audio',
    audience: 'readers who want audio without uploading a file',
    friction:
      'copyable text is ready but your attention is better in audio mode',
    result: 'plain text can become a focused listening session in seconds',
  }),
  buildTextPage({
    slug: 'text-to-speech-read-aloud',
    phrase: 'text to speech read aloud',
    angle: 'testing natural TTS on real paragraphs',
    audience: 'students, editors, and accessibility-minded readers',
    friction: 'built-in voices sound flat across longer paragraphs',
    result:
      'the same read-aloud workflow can scale from a sample to full documents',
  }),
  buildTextPage({
    slug: 'read-text-out-loud',
    phrase: 'read text out loud',
    angle:
      'listening to notes, drafts, and excerpts while keeping the words visible',
    audience: 'people who alternate between reading and listening',
    friction: 'screen fatigue makes another block of text hard to finish',
    result: 'reading can continue even when your eyes need a break',
  }),
  buildTextPage({
    slug: 'text-to-speech-software',
    phrase: 'text to speech software',
    angle: 'evaluating voice quality before choosing a TTS workflow',
    audience: 'professionals comparing text-to-speech options',
    friction: 'software checklists say little about how real documents sound',
    result:
      'you can judge the voice, pacing, and read-along experience directly',
  }),
  buildTextPage({
    slug: 'tts-software',
    phrase: 'TTS software',
    angle: 'quickly testing TTS output with a real passage',
    audience: 'readers who need reliable audio for work or study',
    friction: 'short demos do not reveal how a tool handles real paragraphs',
    result: 'a trial passage becomes a practical audio preview',
  }),
  buildTextPage({
    slug: 'google-text-to-speech-software',
    phrase: 'Google text to speech software',
    angle: 'finding a browser-based alternative for natural read-aloud audio',
    audience: 'people searching for familiar web TTS options',
    friction:
      'you want a simple online workflow without wiring together developer tools',
    result:
      'you can move from search intent to a working audio preview quickly',
  }),
  buildTextPage({
    slug: 'tts-for-computer',
    phrase: 'TTS for computer',
    angle:
      'desktop-friendly reading without installing a heavyweight app first',
    audience: 'laptop and desktop readers',
    friction:
      'you read on a computer but want playback that feels less robotic',
    result:
      'computer reading can shift into listening while the text remains visible',
  }),
  buildTextPage({
    slug: 'ai-text-to-speech-software',
    phrase: 'AI text to speech software',
    angle: 'comparing AI voices on study and work text',
    audience: 'teams and individuals evaluating natural narration',
    friction: 'AI voice quality varies sharply once the text gets technical',
    result:
      'you can test realistic material before moving full workflows into the app',
  }),
  buildTextPage({
    slug: 'speech-synthesis-software',
    phrase: 'speech synthesis software',
    angle: 'turning typed language into listenable long-form audio',
    audience: 'readers who care about clarity, pacing, and voice quality',
    friction: 'synthetic speech becomes tiring when it lacks rhythm',
    result: 'speech synthesis can support real reading instead of a short demo',
  }),
  buildTextPage({
    slug: 'text-to-voice-generator',
    phrase: 'text to voice generator',
    angle: 'generating natural voice from pasted copy',
    audience: 'creators, students, and professionals',
    friction:
      'you need voice output quickly but still want a readable transcript',
    result: 'voice generation stays tied to the original text for review',
  }),
  buildTextPage({
    slug: 'text-to-speech-generator',
    phrase: 'text to speech generator',
    angle: 'creating an audio preview from any paragraph',
    audience: 'people testing TTS for content, notes, or study material',
    friction:
      'you need a fast generator that does not bury the result behind setup',
    result: 'generating speech becomes a lightweight part of reading',
  }),
  buildTextPage({
    slug: 'ai-voice-text-to-speech',
    phrase: 'AI voice text to speech',
    angle: 'hearing pasted text with a more natural AI voice',
    audience: 'readers comparing AI narration quality',
    friction: 'standard TTS voices make important material hard to stay with',
    result: 'AI narration can support focus without separating audio from text',
  }),
  buildTextPage({
    slug: 'text-into-audio',
    phrase: 'text into audio',
    angle: 'converting short text into a listenable read-along',
    audience: 'people who want a quick audio version of written material',
    friction: 'the text is useful but not convenient to read on-screen',
    result: 'written material can become a temporary audio page',
  }),
  buildTextPage({
    slug: 'text-to-audio-online',
    phrase: 'text to audio online',
    angle: 'using a browser-based text audio converter',
    audience: 'readers who want to avoid desktop setup',
    friction: 'you need audio from text now, not after installing software',
    result: 'online conversion can become a real read-along workflow',
  }),
  buildTextPage({
    slug: 'text-to-audio-converter',
    phrase: 'text to audio converter',
    angle: 'converting pasted text with synced playback',
    audience: 'students, writers, and knowledge workers',
    friction: 'audio-only output makes it hard to review the source text',
    result: 'text conversion includes both listening and visual follow-along',
  }),
  buildTextPage({
    slug: 'online-text-to-speech',
    phrase: 'online text to speech',
    angle: 'trying natural TTS directly in the browser',
    audience: 'people who want a no-install text reader',
    friction: 'the fastest option is often the least pleasant to hear',
    result:
      'online text-to-speech can be quick and still usable for real reading',
  }),
  buildTextPage({
    slug: 'tts-for-students',
    phrase: 'TTS for students',
    angle: 'turning notes, handouts, and excerpts into study audio',
    audience: 'students balancing reading with packed schedules',
    friction:
      'assigned reading competes with commuting, chores, and revision time',
    result: 'study material can follow students beyond the desk',
  }),
  buildTextPage({
    slug: 'english-text-to-speech',
    phrase: 'English text to speech',
    angle: 'checking English pacing and pronunciation in long-form text',
    audience: 'students, researchers, and multilingual readers',
    friction: 'English passages include names, citations, or technical terms',
    result: 'English reading practice and review can happen by listening',
  }),
];

const pdfPages = [
  buildPdfPage({
    slug: 'read-pdf',
    phrase: 'read PDF',
    angle: 'turning a PDF into audio without leaving the page',
    audience: 'readers with articles, handouts, and reports',
    friction: 'a PDF is too long to finish in one sitting',
    result: 'PDF reading can happen while your eyes are away from the screen',
  }),
  buildPdfPage({
    slug: 'pdf-read-aloud',
    phrase: 'PDF read aloud',
    angle: 'testing read-aloud audio on real PDF text',
    audience: 'students and professionals with dense PDFs',
    friction: 'PDF pages feel slower than the same content in audio',
    result:
      'you can preview how a document sounds before building a reading routine',
  }),
  buildPdfPage({
    slug: 'pdf-audio-reader',
    phrase: 'PDF audio reader',
    angle: 'listening to PDF files with a synced reading view',
    audience: 'academic and professional PDF readers',
    friction:
      'standard PDF readers are visual-first and awkward for long sessions',
    result: 'PDF audio becomes usable for review, commuting, and study',
  }),
  buildPdfPage({
    slug: 'document-to-audio',
    phrase: 'document to audio',
    angle: 'moving document reading into natural audio',
    audience: 'people with reports, papers, and study files',
    friction: 'documents stack up faster than quiet reading time',
    result: 'document review can shift into listening time',
  }),
  buildPdfPage({
    slug: 'academic-paper-reader',
    phrase: 'academic paper reader',
    angle: 'listening through papers while keeping sections visible',
    audience: 'researchers, graduate students, and faculty',
    friction:
      'citations and technical vocabulary slow down ordinary read-aloud tools',
    result: 'papers become easier to triage, revisit, and discuss',
  }),
  buildPdfPage({
    slug: 'research-paper-audio',
    phrase: 'research paper audio',
    angle: 'creating audio from research papers for focused review',
    audience: 'students and researchers managing literature queues',
    friction: 'reading every paper on-screen is not realistic',
    result:
      'literature review can continue during walks, commutes, and admin work',
  }),
  buildPdfPage({
    slug: 'listen-to-textbooks',
    phrase: 'listen to textbooks',
    angle: 'testing textbook chapters as audio',
    audience: 'students with long assigned chapters',
    friction: 'textbook reading requires more time than the week gives you',
    result: 'chapter review can fit into more parts of the day',
  }),
];

const webPages = [
  buildWebPage({
    slug: 'text-to-speech-for-websites',
    phrase: 'text to speech for websites',
    angle: 'turning website articles into clear audio',
    audience: 'readers with long web articles and documentation',
    friction: 'browser tabs pile up faster than you can read them',
    result: 'web reading can move from tabs into listenable sessions',
  }),
  buildWebPage({
    slug: 'web-page-reader',
    phrase: 'web page reader',
    angle: 'reading web pages aloud without clutter',
    audience: 'people who save articles, guides, and posts for later',
    friction: 'a page has useful text but too much surrounding noise',
    result: 'article reading becomes easier to finish',
  }),
  buildWebPage({
    slug: 'read-web-page-out-loud',
    phrase: 'read web page out loud',
    angle: 'hearing an article while the main text stays available',
    audience: 'multitasking readers and auditory learners',
    friction: 'you want the page content but not the navigation and ads',
    result: 'a web page can become a focused audio read-along',
  }),
  buildWebPage({
    slug: 'websites-that-read-to-you',
    phrase: 'websites that read to you',
    angle: 'testing a website reader on real URLs',
    audience: 'people looking for an online reading assistant',
    friction: 'built-in browser speech reads too much irrelevant page text',
    result: 'saved articles can become a cleaner listening queue',
  }),
  buildWebPage({
    slug: 'online-text-reader',
    phrase: 'online text reader',
    angle: 'reading pasted text or web content aloud online',
    audience: 'readers who want a browser-first audio tool',
    friction: 'you need a simple reader that still sounds natural',
    result: 'online reading can become audio without losing controls',
  }),
];

const mobilePages = [
  buildLinkPage(
    {
      slug: 'text-to-speech-app',
      phrase: 'text to speech app',
      angle: 'mobile reading with natural voices',
      audience: 'people who listen while commuting, walking, or studying',
      friction: 'the reading needs to continue away from the browser',
      result: 'your listening library can travel with you',
    },
    'mobile',
    {
      primaryCta: { label: 'Download for iPhone', href: appStoreHref },
      secondaryCta: { label: 'Download for Android', href: googlePlayHref },
    },
  ),
  buildLinkPage(
    {
      slug: 'tts-app',
      phrase: 'TTS app',
      angle: 'portable TTS for documents and articles',
      audience: 'readers comparing mobile text-to-speech apps',
      friction: 'browser previews are not enough for daily listening',
      result: 'TTS becomes part of a repeatable mobile routine',
    },
    'mobile',
    {
      primaryCta: { label: 'Download for iPhone', href: appStoreHref },
      secondaryCta: { label: 'Download for Android', href: googlePlayHref },
    },
  ),
  buildLinkPage(
    {
      slug: 'pdf-reader-for-android',
      phrase: 'PDF reader for Android',
      angle: 'listening to PDFs from an Android device',
      audience: 'Android users with papers, chapters, and reports',
      friction: 'mobile PDF reading is cramped and easy to abandon',
      result: 'PDFs can move into a mobile audio queue',
    },
    'mobile',
    {
      primaryCta: { label: 'Open Google Play', href: googlePlayHref },
      secondaryCta: { label: 'Try PDF tool online', href: '/pdf-to-audio' },
    },
  ),
  buildLinkPage(
    {
      slug: 'tts-app-for-android',
      phrase: 'TTS app for Android',
      angle: 'Android-first text-to-speech listening',
      audience: 'Android readers who want natural voices',
      friction:
        'system voices are convenient but not ideal for long-form reading',
      result: 'Android listening can stay comfortable across longer material',
    },
    'mobile',
    {
      primaryCta: { label: 'Open Google Play', href: googlePlayHref },
      secondaryCta: { label: 'Try text tool online', href: '/text-to-speech' },
    },
  ),
  buildLinkPage(
    {
      slug: 'android-text-to-speech',
      phrase: 'Android text to speech',
      angle: 'natural TTS for Android study and work',
      audience: 'Android users who read on the go',
      friction: 'you need more than a short system read-aloud toggle',
      result: 'text, PDFs, and articles can become mobile audio',
    },
    'mobile',
    {
      primaryCta: { label: 'Open Google Play', href: googlePlayHref },
      secondaryCta: { label: 'Try text tool online', href: '/text-to-speech' },
    },
  ),
  buildLinkPage(
    {
      slug: 'iphone-read-pdf-aloud',
      phrase: 'iPhone read PDF aloud',
      angle: 'PDF listening from an iPhone',
      audience: 'iPhone and iPad readers with long PDFs',
      friction: 'pinching and scrolling through PDFs is not a good study habit',
      result: 'PDF chapters and papers can follow you as audio',
    },
    'mobile',
    {
      primaryCta: { label: 'Download for iPhone', href: appStoreHref },
      secondaryCta: { label: 'Try PDF tool online', href: '/pdf-to-audio' },
    },
  ),
];

const extensionPages = [
  buildLinkPage(
    {
      slug: 'text-to-speech-chrome-extension',
      phrase: 'text to speech Chrome extension',
      angle: 'turning browser reading into audio',
      audience: 'Chrome users with articles, docs, and research pages',
      friction: 'copying every article into a separate tool slows you down',
      result: 'web pages can enter your listening workflow from the browser',
    },
    'extension',
    {
      primaryCta: { label: 'Add to Chrome', href: chromeExtensionHref },
      secondaryCta: { label: 'Try web tool online', href: '/web-page-reader' },
    },
  ),
  buildLinkPage(
    {
      slug: 'chrome-tts-plugin',
      phrase: 'Chrome TTS plugin',
      angle: 'browser-based TTS from highlighted text and pages',
      audience: 'Chrome users comparing TTS plugins',
      friction: 'generic plugins read too much page clutter',
      result: 'browser reading can become cleaner, natural audio',
    },
    'extension',
    {
      primaryCta: { label: 'Add to Chrome', href: chromeExtensionHref },
      secondaryCta: { label: 'Try text tool online', href: '/text-to-speech' },
    },
  ),
  buildLinkPage(
    {
      slug: 'chromebook-extension',
      phrase: 'Chromebook extension',
      angle: 'read-aloud support from a Chromebook browser',
      audience: 'students and professionals working on Chromebooks',
      friction: 'most reading happens inside the browser',
      result: 'Chromebook tabs can turn into audio without switching devices',
    },
    'extension',
    {
      primaryCta: { label: 'Add to Chrome', href: chromeExtensionHref },
      secondaryCta: { label: 'Try web tool online', href: '/web-page-reader' },
    },
  ),
  buildLinkPage(
    {
      slug: 'tts-extension-for-chrome',
      phrase: 'TTS extension for Chrome',
      angle: 'Chrome-native text-to-speech for web reading',
      audience: 'readers who live in Chrome tabs',
      friction: 'you want audio without leaving the page you are reading',
      result: 'web articles can become part of your Listening library',
    },
    'extension',
    {
      primaryCta: { label: 'Add to Chrome', href: chromeExtensionHref },
      secondaryCta: { label: 'Try text tool online', href: '/text-to-speech' },
    },
  ),
  buildLinkPage(
    {
      slug: 'read-aloud-chrome-extension',
      phrase: 'read aloud Chrome extension',
      angle: 'reading web pages aloud from Chrome',
      audience: 'Chrome users who want read-aloud controls nearby',
      friction: 'switching tools breaks the reading flow',
      result: 'read-aloud audio can start where the article already is',
    },
    'extension',
    {
      primaryCta: { label: 'Add to Chrome', href: chromeExtensionHref },
      secondaryCta: { label: 'Try web tool online', href: '/web-page-reader' },
    },
  ),
  buildLinkPage(
    {
      slug: 'edge-browser-reader',
      phrase: 'Edge browser reader',
      angle: 'browser reading for Chromium-based workflows',
      audience: 'Edge and browser users who want audio support',
      friction: 'web reading is split across articles, docs, and PDFs',
      result:
        'browser content can move into a more flexible listening workflow',
    },
    'extension',
    {
      primaryCta: { label: 'Add extension', href: chromeExtensionHref },
      secondaryCta: { label: 'Try web tool online', href: '/web-page-reader' },
    },
  ),
  buildLinkPage(
    {
      slug: 'mac-os-tts',
      phrase: 'Mac OS TTS',
      angle: 'browser-based TTS on a Mac',
      audience: 'Mac users reading articles and documents in the browser',
      friction: 'system speech is available but not tuned for long-form study',
      result: 'Mac reading can move into natural browser and app audio',
    },
    'extension',
    {
      primaryCta: { label: 'Add to Chrome', href: chromeExtensionHref },
      secondaryCta: { label: 'Try text tool online', href: '/text-to-speech' },
    },
  ),
];

const desktopAndAppPages = [
  buildLinkPage(
    {
      slug: 'word-read-aloud',
      phrase: 'Word read aloud',
      angle: 'listening to Word documents with natural voices',
      audience: 'people reviewing DOC and DOCX files',
      friction:
        'Word drafts and reports need review away from the editing screen',
      result: 'document review can become an audio workflow',
    },
    'app',
    {
      primaryCta: {
        label: 'Open Listening',
        href: '/app/continue',
      },
      secondaryCta: { label: 'Try text tool online', href: '/text-to-speech' },
    },
  ),
  buildLinkPage(
    {
      slug: 'ebook-reader-tts',
      phrase: 'eBook reader TTS',
      angle: 'turning ebook-style reading into audio',
      audience: 'readers with EPUB, MOBI, and PDF material',
      friction: 'long ebook files are hard to finish in screen-only sessions',
      result: 'book-length material can move into a listening routine',
    },
    'app',
    {
      primaryCta: {
        label: 'Open Listening',
        href: '/app/continue',
      },
      secondaryCta: { label: 'Try PDF tool online', href: '/pdf-to-audio' },
    },
  ),
  buildLinkPage(
    {
      slug: 'text-to-speech-for-mac',
      phrase: 'text to speech for Mac',
      angle: 'Mac-friendly audio reading for documents and web pages',
      audience: 'Mac users who read long-form material',
      friction: 'you want natural voices beyond a basic system shortcut',
      result: 'Mac reading can become synced audio across web and mobile',
    },
    'app',
    {
      primaryCta: {
        label: 'Open Listening',
        href: '/app/continue',
      },
      secondaryCta: {
        label: 'Add Chrome extension',
        href: chromeExtensionHref,
      },
    },
  ),
  buildLinkPage(
    {
      slug: 'pdf-reader-for-windows',
      phrase: 'PDF reader for Windows',
      angle: 'PDF audio workflows from a Windows computer',
      audience: 'Windows users with papers, reports, and course files',
      friction: 'desktop PDF reading takes dedicated screen time',
      result: 'Windows PDFs can become audio for study and review',
    },
    'app',
    {
      primaryCta: {
        label: 'Open Listening',
        href: '/app/continue',
      },
      secondaryCta: { label: 'Try PDF tool online', href: '/pdf-to-audio' },
    },
  ),
  buildLinkPage(
    {
      slug: 'text-to-speech-windows',
      phrase: 'text to speech Windows',
      angle: 'natural TTS for Windows reading workflows',
      audience: 'Windows readers comparing TTS options',
      friction:
        'system narration is available but not ideal for long documents',
      result: 'Windows reading can move into a more natural audio workflow',
    },
    'app',
    {
      primaryCta: {
        label: 'Open Listening',
        href: '/app/continue',
      },
      secondaryCta: { label: 'Try text tool online', href: '/text-to-speech' },
    },
  ),
  buildLinkPage(
    {
      slug: 'pdf-reader-for-linux',
      phrase: 'PDF reader for Linux',
      angle: 'browser-based PDF audio that works from Linux',
      audience: 'Linux users reading PDFs for work or research',
      friction:
        'local PDF tooling is flexible but audio workflows are often manual',
      result: 'Linux PDF reading can use the same web-based audio preview',
    },
    'app',
    {
      primaryCta: { label: 'Try PDF tool online', href: '/pdf-to-audio' },
      secondaryCta: {
        label: 'Open Listening',
        href: '/app/continue',
      },
    },
  ),
  buildLinkPage(
    {
      slug: 'ocr-reader-online',
      phrase: 'OCR reader online',
      angle: 'handling scanned documents in a listening workflow',
      audience: 'readers with image-heavy or scanned files',
      friction: 'image-only pages need OCR before text-to-speech can work',
      result:
        'scanned reading material can become usable audio in the full app',
    },
    'app',
    {
      primaryCta: {
        label: 'Open Listening',
        href: '/app/continue',
      },
      secondaryCta: { label: 'Try PDF tool online', href: '/pdf-to-audio' },
    },
  ),
  buildLinkPage(
    {
      slug: 'audiobook-creator',
      phrase: 'audiobook creator',
      angle: 'turning long-form text and documents into listenable chapters',
      audience: 'readers and creators with longer written material',
      friction: 'a single generated clip is not enough for chapter-length work',
      result: 'long-form material can become a structured listening experience',
    },
    'app',
    {
      primaryCta: {
        label: 'Open Listening',
        href: '/app/continue',
      },
      secondaryCta: { label: 'Try text tool online', href: '/text-to-speech' },
    },
  ),
];

const allPages = [
  ...textPages,
  ...pdfPages,
  ...webPages,
  ...mobilePages,
  ...extensionPages,
  ...desktopAndAppPages,
];

const keywordToolPagesBySlug = new Map(
  allPages.map((page) => [page.slug, page]),
);

export function getKeywordToolPage(slug: string) {
  return keywordToolPagesBySlug.get(slug);
}
